import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 12;

export default function AdvertiseSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  const poll = useCallback(
    async (n) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (n >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      setTimeout(() => poll(n + 1), 2000);
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <main data-testid="advertise-success" className="pt-16 md:pt-20 min-h-screen bg-[#F9F8F6]">
      <Seo title="Payment · CPH Highlights" description="Advertising purchase" path="/advertise/success" noindex />
      <div className="max-w-[720px] mx-auto px-5 py-20 text-center">
        {state === "checking" && (
          <div data-testid="pay-checking">
            <Loader2 className="animate-spin mx-auto text-[#DF3226]" size={40} />
            <h1 className="font-display font-900 tracking-tight text-3xl mt-6">Confirming your payment…</h1>
            <p className="font-body text-black/60 mt-2">One moment while we check with Stripe.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="pay-success">
            <CheckCircle2 className="mx-auto text-[#0E9F6E]" size={56} />
            <h1 className="font-display font-900 tracking-tighter text-4xl sm:text-5xl mt-6">You&apos;re live! 🎉</h1>
            <p className="font-body text-base text-black/70 mt-4 max-w-md mx-auto">
              Payment received. Your listing has been placed automatically and now runs for 12 months —
              you&apos;ll see it on your area page, the city map{" "}
              and, depending on your package, in the day-planner and front page. A receipt is on its way from Stripe.
            </p>
            <Link
              to="/"
              data-testid="pay-home"
              className="inline-flex items-center gap-2 mt-8 font-body font-800 bg-[#DF3226] text-white px-7 py-3.5 rounded-full hover:-translate-y-0.5 transition-all"
            >
              Back to start
            </Link>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="pay-pending">
            <XCircle className="mx-auto text-[#F59E0B]" size={48} />
            <h1 className="font-display font-900 tracking-tight text-3xl mt-6">Still processing…</h1>
            <p className="font-body text-black/70 mt-3 max-w-md mx-auto">
              Your payment is taking a little longer to confirm. If you completed checkout, your listing will
              go live shortly. If you were charged and it doesn&apos;t appear, contact us and we&apos;ll sort it out.
            </p>
            <Link to="/advertise" className="inline-flex items-center gap-2 mt-8 font-body font-800 text-[#DF3226]">
              <ArrowLeft size={16} /> Back to Advertise
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
